import { useAuth } from "@clerk/clerk-react";
import { useState, useEffect, useCallback } from "react";

// Hook that wraps Clerk's useAuth with a load timeout fallback.
// Usage:
//  useAuthWithTimeout(10000) // timeout in ms (backwards compatible)
//  useAuthWithTimeout({ timeout: 10000, relaxIfUserId: true })
// Returns: { isLoaded, isSignedIn, degraded, retry, userId }
export default function useAuthWithTimeout(opts = {}) {
  // Backwards compat: allow passing a number as first arg
  const { timeout = typeof opts === "number" ? opts : 10000, relaxIfUserId = false } =
    typeof opts === "number" ? { timeout: opts, relaxIfUserId: false } : opts;

  const { isLoaded: clerkLoaded, isSignedIn: clerkSignedIn, userId } = useAuth();
  const [degraded, setDegraded] = useState(false);

  useEffect(() => {
    if (clerkLoaded) {
      setDegraded(false);
      return;
    }

    const t = setTimeout(() => {
      setDegraded(true);
    }, timeout);

    return () => clearTimeout(t);
  }, [clerkLoaded, timeout]);

  const retry = useCallback(() => {
    setDegraded(false);
    // simplest retry: reload page to re-init Clerk SDK
    if (typeof window !== "undefined") window.location.reload();
  }, []);

  // Determine isLoaded for app usage: treat as loaded when Clerk is loaded or when degraded
  const isLoaded = Boolean(clerkLoaded) || degraded;

  // Conservative isSignedIn: only true when Clerk reports signed in AND loaded.
  // Optional relaxation: if degraded and `relaxIfUserId` is true and we have a `userId`,
  // treat the user as signed in to avoid blocking access in environments where Clerk
  // initialization is delayed but a session cookie/user id is already available.
  let isSignedIn = Boolean(clerkSignedIn) && Boolean(clerkLoaded);
  if (!isSignedIn && degraded && relaxIfUserId && userId) {
    isSignedIn = true;
  }

  return {
    isLoaded,
    isSignedIn,
    degraded,
    retry,
    userId,
  };
}
